import { z } from 'zod';

export { DATA_SOURCES } from '../schemas/scenario';

export enum shortTypes {
  BU = 'BU',
  CO = 'CO',
  CN = 'CN',
  CC = 'CC',
  BZ = 'BZ',
  AL = 'AL',
  USR = 'USR',
}

export const assignmentTypes = {
  BUSINESS_UNITS: shortTypes.BU,
  COMPANY_CODES: shortTypes.CC,
  COUNTRIES: shortTypes.CO,
  CONNECTIONS: shortTypes.CN,
  BUSINESSES: shortTypes.BZ,
  USER: shortTypes.USR,
};


export const assignmentCodesToDescriptiveNames = {
  [shortTypes.BU]: 'businessUnits',
  [shortTypes.CC]: 'companyCodes',
  [shortTypes.CO]: 'countries',
  [shortTypes.CN]: 'connections',
  [shortTypes.BZ]: 'businesses',
  [shortTypes.USR]: 'user',
  [shortTypes.AL]: 'all',
} as const;

export const userOrAllAssignmentCode = 'GENERIC';

export enum AssignmentPrivilegeCode {
  Adjust = 'Adjust',
  Modify = 'ProvCalc',
  ChartEditor = 'ChartEditor',
  SubledgerAdd = 'BSMAdd',
  SubledgerBegBal = 'BSMTopside',
  SubledgerApprove = 'BSMApprove',
  UserAdmin = 'UserAdmin',
}


export const assignmentTypeAll = shortTypes.AL;
export const assignmentTypeUSR = shortTypes.USR;


export enum AssignmentOrPrivilegeSource {
  User = 'U',
  UserRole = 'UR',
  Role = 'R',
}

// 5/26/2023 - Prakash - Once the updates to values below are tested, remove the commented lines below; changes approved by JK on 5/26
// export const assignmentPermissions = {
//   Adjust: 'Adjust',
//   Modify: 'Modify',
//   ChartEditor: 'ChartEditor',
//   SubledgerAdd: 'SubledgerAdd',
//   SubledgerBegBal: 'SubledgerBegBal',
// };

export const assignmentPermissions = {
  Adjust: 'Adjust',
  Modify: 'ProvCalc',
  ChartEditor: 'ChartEditor',
  SubledgerAdd: 'BSMAdd',
  SubledgerBegBal: 'BSMTopside',
};

export const ADD_OR_UPDATE_VALUES = {
  ADD: 'Add',
  UPDATE: 'Update',
  NO_CHANGE: 'No Change',
  FIELD: 'addOrUpdate',
};

export const ADD_OR_UPDATE_SORT_ORDER = [
  ADD_OR_UPDATE_VALUES.ADD,
  ADD_OR_UPDATE_VALUES.UPDATE,
  ADD_OR_UPDATE_VALUES.NO_CHANGE,
];

export const adjustmentTypes = {
  GAAP_TO_STAT_NON_REVERSING: 'GAAP to STAT Non-Reversing',
  GAAP_TO_STAT_REVERSING: 'GAAP to STAT Reversing',
  PERM: 'Perm',
  TEMP: 'Temp',
  TAX_CREDIT: 'After Tax Adjustment',
  STATE_ADJUSTMENT: 'State Adjustment',
  STATE_PERM_MOD: 'State Perm Mod',
  STATE_TEMP_MOD: 'State Temp Mod',
  PRE_TAX_ADJUSTMENT: 'Pre Tax Adjustment',
  DTAXCREDIT: 'Prior Credit Utilization',
};

export const countryTaxTypes = {
  US_STATE: 'US-US1',
  US_FEDERAL: 'US-US2',
};

export const userRoles = {
  SUPER_USER: 'superUser',
  POWER_USER: 'powerUser',
  USER: 'user',
} as const;

export type UserRoles = typeof userRoles[keyof typeof userRoles] | string;

export const groupNames = {
  YES_NO: 'yes_no',
  US_SHAREHOLDER_FLAG: 'us_shareholder_flag',
  LINE_OF_BUSINESS: 'line_of_business',
  SUBLEDGER_VIEW: 'subledger_view',
  US_TAX_TYPE: 'us_tax_type',
  LOCAL_TAX_TYPE: 'local_tax_type',
  TAX_ENTRY_TYPE: 'taxentry_type',
  TAX_ENTRY_ACTIVITY: 'taxentry_activity',
};

export type GroupName = typeof groupNames[keyof typeof groupNames];

export const conversionRateSources = {
  TLS: 'TLS',
};

export const legalEntitiesSources = {
  GLDRPT: 'GLDRPT',
  TLS: 'TLS',
  EO: 'EO',
};

export const companyCodesSources = {
  GLDRPT: 'GLDRPT',
  TLS: 'TLS',
  EO: 'EO',
};

export const countryCodesSources = {
  INC: 'INC',
  RES: 'RES',
};

export const companySources = {
  GLDRPT: 'GLDRPT',
  TLS: 'TLS',
};

export const entryTypes = {
  DEFERRED: 'DEF',
};

export const STATUS_INVALID = -1;

export const ALL = 'ALL';

export const NOL_VALUES = {
  RECORD: 'RECORD',
  UTILIZE: 'UTILIZE',
};

export const adjustmentSavingMethods = {
  Manual: 'Manual',
  Batch: 'Batch',
  Workpaper: 'Workpaper',
};

const yesNoFlagSchema = z.enum(['Y', 'N']);
export type YesNoFlag = z.infer<typeof yesNoFlagSchema>;
export const YesNoFlag = yesNoFlagSchema.enum;

export type YesNoFlagOrBoolean = YesNoFlag | boolean;

export const YES_FLAG = YesNoFlag.Y;
export const NO_FLAG = YesNoFlag.N;
export const ALWAYS_FLAG = 'A';
export const TRUE = 'true';
export const FALSE = 'false';
export const US = 'US';
export const US_US1 = 'US State Income Tax';
export const US_US2 = 'US Federal Income Tax';
export const MAX_ATTACHMENTS = 5;
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
export const MAX_ATTACHMENT_FILE_NAME_LENGTH = 256;
export const ALLOWED_ATTACHMENT_TYPES = [
  'xls', 'xlsx', 'doc', 'docx', 'ppt', 'pptx', 'pdf', 'txt',
  'jpg', 'jpeg', 'png',
] as const;
export const DANGEROUS_ATTACHMENT_NAME_REGEX = /\.(php|phtml|asp|aspx|jsp|exe|bat|sh|cmd|js|mjs|cjs|html?|svg|ps1|dll|vbs|pl|py|rb|war|jar|com|msi|scr|cgi)(\.|$)/i;
export const GLDRPT = companyCodesSources.GLDRPT;
export const TLS = companyCodesSources.TLS;
export const EO = companyCodesSources.EO;

export enum CountryTaxTypeStaticOptions {
  NONE = 'NONE',
  FBOS = 'FBOS',
  ALL = 'ALL'
}

export const ACCOUNT = 'A';
export const DEFERRED_GROUP = 'G';
export const FOOTNOTE = 'F';

export const MULTIPLE_CURRENCIES = 'Multiple';

export const US_COUNTRY_TAX_TYPE_REGEX = /^US-[A-Z]{2}\d$/;

export const SSO_ALPHANUMERIC_REGEX = /^[a-zA-Z0-9]+$/;
export const SSO_ALPHANUMERIC_ERROR = 'SSO must be alphanumeric';


export const groupTypeValues = {
  BUSINESSES: 'BZ',
  BUSINESS_UNITS: 'BU',
  COMPANY_CODES: 'CC',
  COUNTRY_CODES: 'CO',
  LEGAL_ENTITIES: 'LE',
  CUSTOM_GROUP: 'GR',
} as const;


export const BookTypes = {
  GAAP: 'P',
  G2S: 'G',
};

export const FUNCTIONAL_CURRENCY = 'Functional Currency';
